"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProjectState = void 0;
const immutable_1 = require("immutable");
const luxon_1 = require("luxon");
const CreationSet_1 = require("./CreationSet");
const DesignInspirationCase_1 = require("./DesignInspirationCase");
const DynamicSchemaDefinition_1 = require("./DynamicSchemaDefinition");
const DynamicSchemaValue_1 = require("./DynamicSchemaValue");
const Organization_1 = require("./Organization");
const ProjectAsset_1 = require("./ProjectAsset");
const ProjectIssue_1 = require("./ProjectIssue");
const ProjectType_1 = require("./ProjectType");
const User_1 = require("./User");
class Project {
    constructor({ id, versionId, organization, originId, parentId, title, state, demo = false, createdBy, projectType, creationSets, creativeBriefValue, creativeBriefDefinition, creativeBriefUpdatedAt, createdAt, updatedAt, submittedAt, adVideos, canCreativeVisibilityOnProfile, matchingFormat, performanceReports, progressSummary, designInspirationCase, asset, bucketId, assetCategoryValue, assetCategoryDefinition, assetCategoryUpdatedAt, issue, finalized, deliveryDueDate, }) {
        this.toJSON = () => ({
            id: this.id,
            versionId: this.versionId,
            organization: this.organization && this.organization.toJSON(),
            originId: this.originId,
            parentId: this.parentId,
            title: this.title,
            bucketId: this.bucketId,
            state: this.state,
            createdBy: this.createdBy && this.createdBy.toJSON(),
            projectType: this.projectType && this.projectType.toJSON(),
            creationSets: this.creationSets && this.creationSets.toJSON(),
            creativeBriefValue: this.creativeBriefValue && this.creativeBriefValue.toJSON(),
            creativeBriefDefinition: this.creativeBriefDefinition && this.creativeBriefDefinition.toJSON(),
            creativeBriefUpdatedAt: this.creativeBriefUpdatedAt,
            assetCategoryValue: this.assetCategoryValue && this.assetCategoryValue.toJSON(),
            assetCategoryDefinition: this.assetCategoryDefinition && this.assetCategoryDefinition.toJSON(),
            createdAt: this.createdAt && this.createdAt.toISO(),
            updatedAt: this.updatedAt && this.updatedAt.toISO(),
            submittedAt: this.submittedAt && this.submittedAt.toISO(),
            adVideos: this.adVideos && this.adVideos.toJSON(),
            canCreativeVisibilityOnProfile: this.canCreativeVisibilityOnProfile,
            matchingFormat: this.matchingFormat,
            performanceReports: this.performanceReports && this.performanceReports.toJSON(),
            progressSummary: this.progressSummary,
            designInspiration: this.designInspirationCase && this.designInspirationCase.toJSON(),
            issue: this.issue ? this.issue.toJSON() : null,
            deliveryDueDate: this.deliveryDueDate && this.deliveryDueDate.toISO(),
        });
        this.id = id;
        this.versionId = versionId;
        this.organization = organization;
        this.originId = originId;
        this.parentId = parentId;
        this.title = title;
        this.state = state;
        this.demo = demo;
        this.createdBy = createdBy;
        this.projectType = projectType;
        this.creationSets = creationSets;
        this.creativeBriefDefinition = creativeBriefDefinition;
        this.creativeBriefValue = creativeBriefValue;
        this.creativeBriefUpdatedAt = creativeBriefUpdatedAt;
        this.createdAt = createdAt;
        this.updatedAt = updatedAt;
        this.submittedAt = submittedAt;
        this.adVideos = adVideos;
        this.canCreativeVisibilityOnProfile = canCreativeVisibilityOnProfile;
        this.matchingFormat = matchingFormat;
        this.performanceReports = performanceReports;
        this.progressSummary = progressSummary;
        this.designInspirationCase = designInspirationCase;
        this.asset = asset;
        this.bucketId = bucketId;
        this.assetCategoryValue = assetCategoryValue;
        this.assetCategoryDefinition = assetCategoryDefinition;
        this.assetCategoryUpdatedAt = assetCategoryUpdatedAt;
        this.issue = issue;
        this.finalized = finalized;
        this.deliveryDueDate = deliveryDueDate;
    }
}
Project.fromJSON = (json) => {
    const { id, versionId, organization, originId, parentId, title, state, finalized, demo, createdBy, projectType, creationSets, creativeBriefDefinition, creativeBriefValues, creativeBriefUpdatedAt, createdAt, updatedAt, submittedAt, adVideos, canCreativeVisibilityOnProfile, matchingFormat, performanceReports, progressSummary, designInspirationCase, asset, bucketId, assetCategoryValues, assetCategoryDefinition, assetCategoryUpdatedAt, issue, deliveryDueDate, } = Object.assign({}, json);
    if (typeof id !== 'string')
        throw new Error('.id must be a string');
    if (versionId !== undefined && typeof versionId !== 'string' && versionId !== null)
        throw new Error('.versionId must be a string or null');
    if (originId !== undefined && typeof originId !== 'string')
        throw new Error('.originId must be a string');
    if (parentId !== undefined && typeof parentId !== 'string' && parentId !== null)
        throw new Error('.parentId must be a string or null');
    if (title !== undefined && typeof title !== 'string')
        throw new Error('.title must be a string');
    if (creationSets !== undefined && !Array.isArray(creationSets))
        throw new Error('.creationSets must be an array');
    if (state !== undefined && Object.keys(exports.ProjectState).indexOf(state) === -1)
        throw new Error('.state must be any one of project state');
    if (finalized !== undefined && typeof finalized !== 'boolean')
        throw new Error('.finalized must be a boolean');
    if (demo !== undefined && typeof demo !== 'boolean')
        throw new Error('.demo must be a boolean');
    if (creativeBriefUpdatedAt !== undefined &&
        typeof creativeBriefUpdatedAt !== 'string' &&
        creativeBriefUpdatedAt !== null)
        throw new Error('.creativeBriefUpdatedAt must be a string or null');
    if (canCreativeVisibilityOnProfile !== undefined && typeof canCreativeVisibilityOnProfile !== 'boolean')
        throw new Error('.canCreativeVisibilityOnProfile must be a boolean');
    if (bucketId !== undefined && typeof bucketId !== 'string' && bucketId !== null)
        throw new Error('.bucketId must be a string');
    const _organization = organization ? Organization_1.default.fromJSON(organization) : undefined;
    const _createdBy = createdBy ? User_1.DirectorUser.fromJSON(createdBy) : undefined;
    const _projectType = projectType ? ProjectType_1.default.fromJSON(projectType) : undefined;
    const _creationSets = creationSets ? immutable_1.List(creationSets).map(item => CreationSet_1.default.fromJSON(item)) : undefined;
    const _creativeBriefDefinition = creativeBriefDefinition
        ? DynamicSchemaDefinition_1.default.fromJSON(creativeBriefDefinition)
        : undefined;
    const creativeBriefValue = creativeBriefValues && _creativeBriefDefinition
        ? DynamicSchemaValue_1.default.fromJSONAndDefinition(creativeBriefValues, _creativeBriefDefinition)
        : undefined;
    const _performanceReports = performanceReports ? immutable_1.List(performanceReports) : undefined;
    const _designInspirationCase = designInspirationCase
        ? DesignInspirationCase_1.default.fromJSON(designInspirationCase)
        : undefined;
    const _asset = asset ? ProjectAsset_1.default.fromJSON(asset) : undefined;
    const _adVideos = adVideos ? immutable_1.List(adVideos) : undefined;
    const _creativeBriefUpdatedAt = creativeBriefUpdatedAt ? luxon_1.DateTime.fromISO(creativeBriefUpdatedAt) : undefined;
    const _assetCategoryUpdatedAt = assetCategoryUpdatedAt ? luxon_1.DateTime.fromISO(assetCategoryUpdatedAt) : undefined;
    const _assetCategoryDefinition = assetCategoryDefinition
        ? DynamicSchemaDefinition_1.default.fromJSON(assetCategoryDefinition)
        : undefined;
    const assetCategoryValue = assetCategoryValues && _assetCategoryDefinition
        ? DynamicSchemaValue_1.default.fromJSONAndDefinition(assetCategoryValues, _assetCategoryDefinition)
        : undefined;
    return new Project({
        id,
        versionId,
        organization: _organization,
        originId,
        parentId,
        title,
        state: state,
        finalized,
        demo,
        createdBy: _createdBy,
        projectType: _projectType,
        creationSets: _creationSets,
        creativeBriefDefinition: _creativeBriefDefinition,
        creativeBriefValue,
        creativeBriefUpdatedAt: _creativeBriefUpdatedAt,
        createdAt: luxon_1.DateTime.fromISO(createdAt),
        updatedAt: luxon_1.DateTime.fromISO(updatedAt),
        submittedAt: submittedAt === null ? null : luxon_1.DateTime.fromISO(submittedAt),
        adVideos: _adVideos,
        canCreativeVisibilityOnProfile,
        matchingFormat,
        performanceReports: _performanceReports,
        progressSummary,
        designInspirationCase: _designInspirationCase,
        asset: _asset,
        bucketId,
        assetCategoryDefinition: _assetCategoryDefinition,
        assetCategoryValue,
        assetCategoryUpdatedAt: _assetCategoryUpdatedAt,
        issue: issue ? ProjectIssue_1.default.fromJSON(issue) : null,
        deliveryDueDate: luxon_1.DateTime.fromISO(deliveryDueDate),
    });
};
exports.ProjectState = {
    draft: 'draft',
    submitted: 'submitted',
    assigned: 'assigned',
    canceled: 'canceled',
    finalized: 'finalized',
};
exports.default = Project;
